import { useQuery } from "@tanstack/react-query";
import { Fragment, type MouseEvent, type ReactNode } from "react";
import { api, mediaUrl, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { ErrorBox } from "../../components/ErrorBox";
import { AppLink, CellView } from "./cells";
import { SUBGROUP_LABEL } from "./params";

type Page = Schemas["BoardPage"];
type Item = Schemas["BoardItem"];
type Presence = Schemas["PresenceOut"];

export type SortState = { sort?: string; desc: boolean };

/** Row checkboxes (optional): which rows are selected and how to flip one / all of them. */
export type RowSelect = {
  ids: number[];
  toggle: (id: number) => void;
  toggleAll?: () => void;
  testId: (id: number) => string;
};

/** An extra trailing column drawn by the caller (e.g. per-row results of a bulk action). */
export type ExtraColumn = { title: string; cell: (item: Item) => ReactNode };

export function presenceFor(presence: Presence[], entity: string, item: Item): Presence[] {
  return presence.filter((p) => (item.href && p.page === item.href) || (p.entity === entity && p.entity_id === item.id));
}

function SortHeader({ colKey, title, sortable, sort, onSort }: {
  colKey: string;
  title: string;
  sortable: boolean;
  sort?: SortState;
  onSort?: (key: string) => void;
}) {
  const active = sort?.sort === colKey;
  const ariaSort = active ? (sort?.desc ? "descending" : "ascending") : undefined;
  if (!sortable || !onSort) {
    return <th scope="col">{title}</th>;
  }
  return (
    <th scope="col" aria-sort={ariaSort}>
      <button type="button" className="ws7-sort" data-testid={`sort-${colKey}`} aria-label={`Sort by ${title}`}
        onClick={() => onSort(colKey)}>
        {title}
        {active && <span aria-hidden="true">{sort?.desc ? " ↓" : " ↑"}</span>}
      </button>
    </th>
  );
}

export function BoardTable({ page, group, presence = [], open = [], onToggle, sort, onSort, label, select, extra,
  onRowClick, current }: {
  page: Page;
  group: string;
  presence?: Presence[];
  open?: number[];
  onToggle?: (item: Item) => void;
  sort?: SortState;
  onSort?: (key: string) => void;
  label: string;
  select?: RowSelect;
  extra?: ExtraColumn;
  /** A click on the row (not on its controls or other links; its own title link included) */
  onRowClick?: (item: Item) => void;
  /** The row shown as current (aria-current). */
  current?: number | null;
}) {
  const expandable = !!page.subgroup && !!onToggle;
  const subLabel = page.subgroup ? (SUBGROUP_LABEL[page.subgroup] ?? page.subgroup) : "";
  const colCount = page.columns.length + 1 + (expandable ? 1 : 0) + (select ? 1 : 0) + (extra ? 1 : 0);
  const allOn = !!select && page.items.length > 0 && page.items.every((it) => select.ids.includes(it.id));
  // capture phase: runs before a router Link's own click handler, so preventDefault keeps it from navigating
  const rowClick = (it: Item) => (e: MouseEvent<HTMLTableRowElement>) => {
    if (!onRowClick) return;
    const t = e.target as Element;
    if (t.closest("input, button, select, textarea, label")) return;
    const a = t.closest("a");
    if (a && a.getAttribute("href") !== it.href) return;
    if (a) e.preventDefault();
    onRowClick(it);
  };
  return (
    <table className="ws7-table ws7-board" aria-label={label} data-testid={`board-table-${group}`}>
      <thead>
        <tr>
          {select && (
            <th scope="col">
              {select.toggleAll ? (
                <input type="checkbox" checked={allOn} onChange={select.toggleAll} aria-label="Select all rows"
                  data-testid={`select-all-${group}`} />
              ) : <span className="ws7-visually-hidden">Select</span>}
            </th>
          )}
          {expandable && <th scope="col"><span className="ws7-visually-hidden">Subitems</span></th>}
          <SortHeader colKey="title" title="Title" sortable sort={sort} onSort={onSort} />
          {page.columns.map((c) => (
            <SortHeader key={c.key} colKey={c.key} title={c.title} sortable={c.sortable} sort={sort} onSort={onSort} />
          ))}
          {extra && <th scope="col">{extra.title}</th>}
        </tr>
      </thead>
      <tbody>
        {page.items.map((it) => {
          const isOpen = open.includes(it.id);
          const here = presenceFor(presence, page.entity, it);
          return (
            <Fragment key={it.id}>
              <tr data-testid={`row-${group}-${it.id}`} onClickCapture={onRowClick ? rowClick(it) : undefined}
                aria-current={current === it.id ? "true" : undefined}
                className={onRowClick ? "ws7-row-click" : undefined}>
                {select && (
                  <td>
                    <input type="checkbox" checked={select.ids.includes(it.id)} onChange={() => select.toggle(it.id)}
                      aria-label={`Select ${it.title}`} data-testid={select.testId(it.id)} />
                  </td>
                )}
                {expandable && (
                  <td>
                    {it.sub_count != null && (
                      <button type="button" data-testid={`expand-${it.id}`} aria-expanded={isOpen}
                        aria-controls={`sub-${group}-${it.id}`}
                        aria-label={`${isOpen ? "Hide" : "Show"} ${subLabel} (${it.sub_count}) of ${it.title}`}
                        onClick={() => onToggle?.(it)}>
                        {isOpen ? "Hide" : "Show"} {subLabel} ({it.sub_count})
                      </button>
                    )}
                  </td>
                )}
                <td className="ws7-title">
                  {it.thumb_media_id != null && (
                    <img className="ws7-row-thumb" data-testid={`thumb-${group}-${it.id}`} src={mediaUrl(it.thumb_media_id)}
                      alt={`Cover of ${it.title}`} loading="lazy" width={40} height={40} />
                  )}
                  {it.href ? <AppLink href={it.href}>{it.title}</AppLink> : <span>{it.title}</span>}
                  {it.subtitle && <span className="ws7-subtitle">{it.subtitle}</span>}
                  {here.length > 0 && (
                    <ul className="ws7-presence" aria-label={`Here now: ${here.map((p) => p.actor).join(", ")}`}>
                      {here.map((p) => <li key={p.actor}>{p.actor}</li>)}
                    </ul>
                  )}
                </td>
                {page.columns.map((c) => (
                  <td key={c.key} className={c.kind === "number" ? "num" : undefined} data-col={c.key}>
                    <CellView kind={c.kind} cell={it.cells[c.key]} />
                  </td>
                ))}
                {extra && <td data-col="extra">{extra.cell(it)}</td>}
              </tr>
              {expandable && isOpen && (
                <tr id={`sub-${group}-${it.id}`}>
                  <td className="ws7-sub" colSpan={colCount}>
                    <Subitems group={group} item={it} subLabel={subLabel} presence={presence} />
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function Subitems({ group, item, subLabel, presence }: { group: string; item: Item; subLabel: string; presence: Presence[] }) {
  const q = useQuery({
    queryKey: qk.subitems(group, item.id),
    queryFn: () => unwrap(api.GET("/api/board/{group}/{item_id}/subitems", { params: { path: { group, item_id: item.id } } })),
  });
  if (q.isPending) return <p className="ws7-muted">Loading {subLabel}…</p>;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (q.data.items.length === 0) return <p className="ws7-muted">No {subLabel} yet.</p>;
  return <BoardTable page={q.data} group={q.data.group} presence={presence} label={`${subLabel} of ${item.title}`} />;
}
